/**
 * language-select.tsx — Chọn ngôn ngữ giao diện.
 *
 * `LanguageSelect`  : ô chọn (Cài đặt cá nhân, trang đăng nhập, Thông tin công ty).
 * `LanguageSubmenu` : hàng "Ngôn ngữ: Tiếng Việt >" trong menu tài khoản (kiểu SaleSmartly).
 * `LanguageMenuButton`: nút nhỏ "VI ▾" góc hộp đăng nhập / đăng ký (chưa đăng nhập — chỉ lưu trên trình duyệt).
 * Tên hiển thị = tên bản ngữ; dòng phụ = tên theo ngôn ngữ đang dùng (Intl.DisplayNames).
 */
import { Languages } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from '@/components/ui/dropdown-menu'
import { LANGUAGES, findLanguage } from '@/locales/languages'
import { useLanguage } from '@/hooks/use-ui-language'
import { cn } from '@/lib/utils'

/** Tên ngôn ngữ `code` viết bằng ngôn ngữ `inLang` (rơi về tên tiếng Việt nếu trình duyệt không hỗ trợ). */
export function languageDisplayName(code: string, inLang: string): string {
  try {
    const dn = new Intl.DisplayNames([inLang], { type: 'language' })
    return dn.of(code) ?? findLanguage(code)?.viName ?? code
  } catch {
    return findLanguage(code)?.viName ?? code
  }
}

function LanguageLabel({ code, current, withHint = true }: { code: string; current: string; withHint?: boolean }) {
  const lang = findLanguage(code)
  if (!lang) return <>{code}</>
  const hint = languageDisplayName(code, current)
  return (
    <span className="flex min-w-0 items-center gap-2">
      <span aria-hidden className="shrink-0">{lang.flag}</span>
      <span className="shrink-0">{lang.nativeName}</span>
      {withHint && hint !== lang.nativeName && (
        <span className="min-w-0 truncate text-xs text-muted-foreground">{hint}</span>
      )}
    </span>
  )
}

/**
 * Ô chọn ngôn ngữ. Mặc định đổi ngôn ngữ giao diện của CHÍNH người dùng; truyền
 * `value` + `onChange` để dùng như ô chọn thường (vd mặc định tổ chức).
 */
export function LanguageSelect({
  className,
  value,
  onChange,
  allowEmpty,
  emptyLabel,
  disabled,
  id,
}: {
  className?: string
  value?: string | null
  onChange?: (code: string | null) => void
  /** Cho phép chọn "không đặt" (giá trị null) với nhãn `emptyLabel`. */
  allowEmpty?: boolean
  emptyLabel?: string
  disabled?: boolean
  id?: string
}) {
  const { t } = useTranslation()
  const { current, setLanguage, saving } = useLanguage()
  const controlled = onChange !== undefined
  const selected = controlled ? (value ?? '__none') : current

  const handle = (v: string) => {
    if (controlled) onChange(v === '__none' ? null : v)
    else void setLanguage(v)
  }

  return (
    <Select value={selected} onValueChange={handle} disabled={disabled || (!controlled && saving)}>
      <SelectTrigger id={id} className={cn('h-9 w-full sm:w-72', className)} aria-label={t('language.label')}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {allowEmpty && (
          <SelectItem value="__none">{emptyLabel ?? t('language.followBrowser')}</SelectItem>
        )}
        {LANGUAGES.map((l) => (
          <SelectItem key={l.code} value={l.code} lang={l.code}>
            <LanguageLabel code={l.code} current={current} />
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

/** Hàng "Ngôn ngữ" + menu con trong menu tài khoản. */
export function LanguageSubmenu() {
  const { t } = useTranslation()
  const { current, setLanguage } = useLanguage()
  const cur = findLanguage(current)
  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger>
        <Languages className="me-2 h-4 w-4 text-muted-foreground" />
        <span className="flex-1">{t('language.label')}</span>
        <span className="pe-1 ps-2 text-xs text-muted-foreground">{cur?.nativeName}</span>
      </DropdownMenuSubTrigger>
      <DropdownMenuSubContent className="max-h-[min(28rem,var(--radix-dropdown-menu-content-available-height,28rem))] w-72 overflow-y-auto">
        <DropdownMenuRadioGroup value={current} onValueChange={(v) => void setLanguage(v)}>
          {LANGUAGES.map((l) => (
            <DropdownMenuRadioItem key={l.code} value={l.code} lang={l.code}>
              <LanguageLabel code={l.code} current={current} />
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  )
}

/** Nút nhỏ mã ngôn ngữ (VI, EN…) mở danh sách ngôn ngữ — góc hộp đăng nhập. */
export function LanguageMenuButton({ className }: { className?: string }) {
  const { t } = useTranslation()
  const { current, setLanguage } = useLanguage()
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label={t('language.label')}
        title={findLanguage(current)?.nativeName}
        className={cn(
          'flex items-center gap-1 rounded-md border border-border/70 bg-background/70 px-2 py-0.5 text-xs font-medium text-muted-foreground outline-none hover:text-foreground',
          className,
        )}
      >
        <Languages className="h-3.5 w-3.5" />
        {current.split('-')[0].toUpperCase()}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="z-[70] w-64">
        <DropdownMenuRadioGroup value={current} onValueChange={(v) => void setLanguage(v)}>
          {LANGUAGES.map((l) => (
            <DropdownMenuRadioItem key={l.code} value={l.code} lang={l.code}>
              <LanguageLabel code={l.code} current={current} />
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
